import {
  WAF_STATUS,
  type WafStatusForDomain,
} from '../domain-manager/waf-status';

export type ApplicationUrlWafStatusTone =
  | 'muted'
  | 'success'
  | 'error'
  | 'warning';

export type ApplicationUrlWafStatusIcon = 'loading' | 'check' | 'alert';

export type ApplicationUrlWafStatusMessageKey =
  | 'verificationInProgress'
  | 'noWafDetected'
  | 'wafDetected'
  | 'urlUnreachable'
  | 'untrustedCertificate'
  | 'verificationError';

export type ApplicationUrlWafStatusTooltipKey =
  | 'tooltip'
  | 'reachabilityTooltip'
  | 'untrustedCertificateTooltip';

export type ApplicationUrlWafStatusDisplay = {
  messageKey: ApplicationUrlWafStatusMessageKey;
  tooltipKey: ApplicationUrlWafStatusTooltipKey;
  tone: ApplicationUrlWafStatusTone;
  icon: ApplicationUrlWafStatusIcon;
};

type DisplayedWafStatus = Exclude<WafStatusForDomain, typeof WAF_STATUS.idle>;

const DISPLAY_BY_WAF_STATUS: Record<
  DisplayedWafStatus,
  ApplicationUrlWafStatusDisplay
> = {
  [WAF_STATUS.pending]: {
    messageKey: 'verificationInProgress',
    tooltipKey: 'tooltip',
    tone: 'muted',
    icon: 'loading',
  },
  [WAF_STATUS.noWaf]: {
    messageKey: 'noWafDetected',
    tooltipKey: 'tooltip',
    tone: 'success',
    icon: 'check',
  },
  [WAF_STATUS.wafDetected]: {
    messageKey: 'wafDetected',
    tooltipKey: 'tooltip',
    tone: 'error',
    icon: 'alert',
  },
  [WAF_STATUS.unreachable]: {
    messageKey: 'urlUnreachable',
    tooltipKey: 'reachabilityTooltip',
    tone: 'muted',
    icon: 'alert',
  },
  [WAF_STATUS.untrustedCertificate]: {
    messageKey: 'untrustedCertificate',
    tooltipKey: 'untrustedCertificateTooltip',
    tone: 'warning',
    icon: 'alert',
  },
  [WAF_STATUS.error]: {
    messageKey: 'verificationError',
    tooltipKey: 'reachabilityTooltip',
    tone: 'muted',
    icon: 'alert',
  },
};

export type WafCheckOutcome = {
  wafDetected?: boolean;
  urlReachable?: boolean;
  untrustedCertificate?: boolean;
  error?: string;
};

export const toWafStatusFromCheckOutcome = (
  outcome: WafCheckOutcome | null | undefined,
): WafStatusForDomain => {
  if (!outcome) {
    return WAF_STATUS.error;
  }

  if (outcome.urlReachable === false) {
    return WAF_STATUS.unreachable;
  }

  if (outcome.error && outcome.wafDetected === undefined) {
    return WAF_STATUS.error;
  }

  if (outcome.wafDetected) {
    return WAF_STATUS.wafDetected;
  }

  if (outcome.untrustedCertificate) {
    return WAF_STATUS.untrustedCertificate;
  }

  return WAF_STATUS.noWaf;
};

export const toWafCheckableUrl = (input: string): string => {
  const trimmed = input.trim();

  return /^(https?:\/\/)/i.test(trimmed) ? trimmed : `https://${trimmed}`;
};

export const resolveApplicationUrlWafStatusDisplay = ({
  wafStatus,
  url,
}: {
  wafStatus: WafStatusForDomain;
  url: string | undefined;
}): ApplicationUrlWafStatusDisplay | null => {
  if (wafStatus === WAF_STATUS.idle || !url?.trim()) {
    return null;
  }

  return DISPLAY_BY_WAF_STATUS[wafStatus];
};
